import React, { useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import styled from '@emotion/styled'
import { ModalWindow, ModalFooter, Groupbox } from 'view/components/panels'
import { Locale, Translation } from 'locale'
import { DialogRow, DialogField, NumericInputField, DialogGrid } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import {
  HaircutEntry,
  EntityType,
  HaircutBand,
  WorkflowActions,
  ReferenceDataManagementEvent,
  EntityTypeEnum,
  EntityState,
  ValidationIssue
} from 'api'
import {
  HaircutOrganisationSelectboxField,
  HaircutTradingEntitySelectboxField,
  HaircutCounterpartyOrganisationSelectboxField,
  HaircutCounterpartyTradingEntitySelectboxField,
  HaircutProductSectorSelectboxField,
  HaircutProductSelectboxField
} from 'view/shared/entity'
import { HaircutBandHelper } from 'model'
import * as constants from 'view/styles/constants'
import { getHaircutFormatOptions } from 'view/shared/datagrid'
import { UnhappyError, ValidationIssuesContainer, withUnhappyModalBoundary } from 'view/shared/error'
import { HaircutTypeSelectboxField } from 'view/shared/entity/haircutOptions/HaircutTypeSelectbox'
import {
  useAllEntities,
  useCurrentOrganisation,
  useHaircutReferenceData,
  useReferenceDataAvailableEvents,
  useUpdateHandler
} from 'view/hooks'
import { ClonedComponentByShowModal } from 'view/shared/modal'
import { populateValuesByBand } from '../utils'

interface HaircutDialogProps extends ClonedComponentByShowModal {
  workflowId: string
  entityId: string
}

const Content = styled(Groupbox)`
  height: 400px;
  width: 510px;
  padding: 20px 20px 0 20px;
  overflow: auto;
`

const EMPTY_VALIDATIONS: ValidationIssue[] = []

export const HaircutDialog: React.FC<HaircutDialogProps> = withUnhappyModalBoundary(({ workflowId, onClose, entityId }) => {
  const haircutEntryEntity = useHaircutReferenceData({ workflowId, entityId })
  const traderOrganisation = useCurrentOrganisation()

  if (!haircutEntryEntity?.data) {
    throw new UnhappyError('Haircut Entry Entity not found.')
  }

  const dispatch = useDispatch()

  const haircutEntry = haircutEntryEntity.data

  const availableEvents = useReferenceDataAvailableEvents(workflowId)

  const readOnly = useMemo(() => !availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE), [availableEvents])

  const validationIssues = haircutEntryEntity?.validationIssues ?? EMPTY_VALIDATIONS
  const hasValidationIssues = !!validationIssues && validationIssues.length > 0

  const onUpdateHaircutEntry = useCallback(
    (partialHaircutEntry: Partial<HaircutEntry>) => {
      dispatch(
        WorkflowActions.createEntityUpdateReferenceData<HaircutEntry>({
          entityId: haircutEntry.id,
          entityType: EntityTypeEnum.HAIRCUT_ENTRY,
          updatedEntity: { ...haircutEntry, ...partialHaircutEntry },
          workflowId
        })
      )
    },
    [haircutEntry, workflowId, dispatch]
  )

  const updateHandler = useUpdateHandler(onUpdateHaircutEntry)

  const updateResponderOrganisationIdHandler = useCallback(
    (value: string) => {
      if (value === haircutEntry.responderOrganisationId) return

      onUpdateHaircutEntry({
        responderOrganisationId: value,
        initiatorOrganisationId: null,
        initiatorTradingEntityId: null,
        responderTradingEntityId: null,
        productId: null,
        productSectorId: null,
        collateralMaturityMinYears: null,
        collateralMaturityMaxYears: null
      })
    },
    [haircutEntry.responderOrganisationId, onUpdateHaircutEntry]
  )

  const updateTradingEntityIdHandler = useCallback(
    (value: string) => {
      if (value === haircutEntry.responderTradingEntityId) return

      onUpdateHaircutEntry({
        responderTradingEntityId: value,
        initiatorOrganisationId: null,
        initiatorTradingEntityId: null,
        productId: null,
        productSectorId: null,
        collateralMaturityMinYears: null,
        collateralMaturityMaxYears: null
      })
    },
    [haircutEntry.responderTradingEntityId, onUpdateHaircutEntry]
  )

  const updateCounterpartyOrganisationIdHandler = useCallback(
    (value: string) => {
      if (value === haircutEntry.initiatorOrganisationId) return

      onUpdateHaircutEntry({
        initiatorOrganisationId: value,
        initiatorTradingEntityId: null,
        productId: null,
        productSectorId: null,
        collateralMaturityMinYears: null,
        collateralMaturityMaxYears: null
      })
    },
    [haircutEntry.initiatorOrganisationId, onUpdateHaircutEntry]
  )

  const updateCounterpartyTradingEntityIdHandler = useCallback(
    (value: string) => {
      if (value === haircutEntry.initiatorTradingEntityId) return

      onUpdateHaircutEntry({
        initiatorTradingEntityId: value,
        productId: null,
        productSectorId: null,
        collateralMaturityMinYears: null,
        collateralMaturityMaxYears: null
      })
    },
    [haircutEntry.initiatorTradingEntityId, onUpdateHaircutEntry]
  )

  const updateProductIdHandler = useCallback(
    (value: string) => {
      if (value === haircutEntry.productId) return

      onUpdateHaircutEntry({
        productId: value,
        productSectorId: null,
        collateralMaturityMinYears: null,
        collateralMaturityMaxYears: null
      })
    },
    [haircutEntry.productId, onUpdateHaircutEntry]
  )

  const updateProductSectorIdHandler = useCallback(
    (value: string) => {
      if (value === haircutEntry.productSectorId) return

      onUpdateHaircutEntry({
        productSectorId: value,
        collateralMaturityMinYears: null,
        collateralMaturityMaxYears: null
      })
    },
    [haircutEntry.productSectorId, onUpdateHaircutEntry]
  )

  const onSave = useCallback(() => {
    dispatch(WorkflowActions.createSubmitWorkflow(workflowId))
    onClose?.()
  }, [dispatch, onClose, workflowId])

  const handleClose = useCallback(() => {
    dispatch(WorkflowActions.createCancelWorkflow(workflowId))
    onClose?.()
  }, [dispatch, onClose, workflowId])

  return (
    <ModalWindow
      title={Locale.translate(
        haircutEntryEntity.entityState === EntityState.NEW ? Translation.TC_ADD_NEW_HAIRCUT : Translation.TC_EDIT_HAIRCUT
      )}>
      <Content>
        <ValidationIssuesContainer>
          {validationIssues
            .filter(issue => issue.fieldName === 'haircut')
            .map((issue, index) => (
              <li key={index}>{issue.errorMessage}</li>
            ))}
        </ValidationIssuesContainer>
        <DialogRow>
          <DialogField>
            <HaircutOrganisationSelectboxField
              dataTestId={'HaircutDialog-HaircutOrganisationSelectboxField-MyFirm'}
              label={
                traderOrganisation
                  ? Locale.translate(Translation.SC_HAIRCUT_MY_FIRM_LABEL)
                  : Locale.translate(Translation.SC_HAIRCUT_SELL_FIRM_LABEL)
              }
              selectedOrganisationId={haircutEntry.responderOrganisationId}
              onUpdate={updateResponderOrganisationIdHandler}
              readOnly={!!traderOrganisation}
              validationIssues={validationIssues}
            />
          </DialogField>
          <DialogField>
            <HaircutTradingEntitySelectboxField
              dataTestId={'HaircutDialog-HaircutTradingEntitySelectboxField-MyEntity'}
              fieldName="responderTradingEntityId"
              label={
                traderOrganisation
                  ? Locale.translate(Translation.SC_HAIRCUT_MY_ENTITY_LABEL)
                  : Locale.translate(Translation.SC_HAIRCUT_SELL_ENTITY_LABEL)
              }
              selectedOrganisationId={haircutEntry.responderOrganisationId}
              selectedTradingEntityId={haircutEntry.responderTradingEntityId}
              onUpdate={updateTradingEntityIdHandler}
              validationIssues={validationIssues}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <HaircutCounterpartyOrganisationSelectboxField
              dataTestId={'HaircutDialog-HaircutCounterpartyOrganisationSelectboxField-CounterpartyFirm'}
              label={Locale.translate(Translation.SC_HAIRCUT_COUNTERPARTY_FIRM_LABEL)}
              selectedTradingEntityId={haircutEntry.responderTradingEntityId}
              selectedOrganisationId={haircutEntry.responderOrganisationId}
              selectedCounterpartyOrganisationId={haircutEntry.initiatorOrganisationId}
              onUpdate={updateCounterpartyOrganisationIdHandler}
              validationIssues={validationIssues}
            />
          </DialogField>
          <DialogField>
            <HaircutCounterpartyTradingEntitySelectboxField
              dataTestId={'HaircutDialog-HaircutCounterpartyTradingEntitySelectboxField-Counterparty'}
              fieldName="initiatorTradingEntityId"
              label={Locale.translate(Translation.SC_HAIRCUT_COUNTERPARTY_LABEL)}
              selectedCounterpartyTradingEntityId={haircutEntry.initiatorTradingEntityId}
              selectedCounterpartyOrganisationId={haircutEntry.initiatorOrganisationId}
              selectedTradingEntityId={haircutEntry.responderTradingEntityId}
              onUpdate={updateCounterpartyTradingEntityIdHandler}
              validationIssues={validationIssues}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <HaircutProductSelectboxField
              dataTestId={'HaircutDialog-HaircutProductSelectboxField-Product'}
              fieldName="productId"
              label={Locale.translate(Translation.SC_HAIRCUT_PRODUCT_LABEL)}
              selectedOrganisationId={haircutEntry.responderOrganisationId}
              selectedTradingEntityId={haircutEntry.responderTradingEntityId}
              selectedProductId={haircutEntry.productId}
              onUpdate={updateProductIdHandler}
              validationIssues={validationIssues}
            />
          </DialogField>
          <DialogField>
            <HaircutProductSectorSelectboxField
              dataTestId={'HaircutDialog-HaircutProductSectorSelectboxField-ProductSector'}
              fieldName="productSectorId"
              label={Locale.translate(Translation.SC_HAIRCUT_PRODUCT_SECTOR_LABEL)}
              selectedTradingEntityId={haircutEntry.responderTradingEntityId}
              selectedOrganisationId={haircutEntry.responderOrganisationId}
              selectedProductId={haircutEntry.productId}
              selectedProductSectorId={haircutEntry.productSectorId}
              onUpdate={updateProductSectorIdHandler}
              validationIssues={validationIssues}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <NumericInputField
              dataTestId={'HaircutDialog-NumericInputField-CollateralMaturityMinYears'}
              fieldName="collateralMaturityMinYears"
              min={0}
              maxLength={2}
              isInteger={true}
              label={Locale.translate(Translation.TC_HAIRCUT_COLLATERAL_BAND_MIN)}
              onUpdate={updateHandler('collateralMaturityMinYears')}
              value={haircutEntry.collateralMaturityMinYears}
              validationIssues={validationIssues}
            />
          </DialogField>
          <DialogField>
            <NumericInputField
              dataTestId={'HaircutDialog-NumericInputField-CollateralMaturityMaxYears'}
              fieldName="collateralMaturityMaxYears"
              min={0}
              maxLength={2}
              isInteger={true}
              label={Locale.translate(Translation.TC_HAIRCUT_COLLATERAL_BAND_MAX)}
              onUpdate={updateHandler('collateralMaturityMaxYears')}
              value={haircutEntry.collateralMaturityMaxYears}
              validationIssues={validationIssues}
            />
          </DialogField>
          <DialogField>
            <HaircutTypeSelectboxField
              dataTestId={'HaircutDialog-HaircutCollateralBandSelectboxField-HaircutType'}
              fieldName="haircutType"
              label={Locale.translate(Translation.TC_COLLATERAL_HAIRCUT)}
              onUpdate={updateHandler('haircutType')}
              value={haircutEntry.haircutType}
              validationIssues={validationIssues}
              canClear={false}
            />
          </DialogField>
        </DialogRow>
        <DialogGrid numberOfColumns={3}>
          <HaircutBands
            valuesByBand={haircutEntry.valuesByBand}
            onChange={updateHandler('valuesByBand')}
            validationIssues={validationIssues}
          />
        </DialogGrid>
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton dataTestId={'HaircutDialog-TextButton-ButtonCancel'} type={TextButtonType.Tertiary} onClick={handleClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              dataTestId={'HaircutDialog-TextButton-ButtonSave'}
              type={TextButtonType.Primary}
              disabled={readOnly || hasValidationIssues}
              onClick={onSave}>
              {Locale.translate(Translation.TC_BUTTON_SAVE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
})

const HaircutBands = (props: {
  valuesByBand: HaircutEntry['valuesByBand']
  onChange: (valuesByBand: HaircutEntry['valuesByBand']) => void
  validationIssues: ValidationIssue[]
}) => {
  const haircutBands = useAllEntities(EntityType.HAIRCUT_BAND)

  const haircutFieldnameHelper = (haircutBand: HaircutBand) => {
    const minDays = `min days = ${haircutBand.minDays}`
    const maxDays = haircutBand.maxDays ? ` max days = ${haircutBand.maxDays}` : ''
    return minDays.concat(maxDays)
  }

  return (
    <>
      {haircutBands.map(band => (
        <NumericInputField
          key={band.id}
          fieldName={haircutFieldnameHelper(band)}
          label={HaircutBandHelper.getName(band)}
          value={props.valuesByBand?.[band.id]}
          min={0}
          maxLength={constants.defaultMaxTextFieldLength}
          formatOptions={{
            ...getHaircutFormatOptions(),
            maxDp: 3
          }}
          onUpdate={value => {
            const valuesByBand = populateValuesByBand(props.valuesByBand, value, band.id, haircutBands)

            props.onChange({ ...valuesByBand, [band.id]: value })
          }}
          validationIssues={props.validationIssues}
          dataTestId={`HaircutDialog-NumericInputField-${HaircutBandHelper.getName(band)}`}
        />
      ))}
    </>
  )
}
